/**
 * Clause authoring on the DOCX-native editor.
 *
 * ## Why this replaces the CKEditor version
 *
 * Clause authoring was the last surface in the app still running CKEditor. Its
 * output was HTML, so every insert into a document had to be converted
 * HTML → OOXML on the fly — a lossy step for exactly the formatting clauses care
 * about (list numbering, indents, fonts). Authoring in the same engine the
 * document uses removes that conversion: `clauseBody.ts` already classifies a
 * `PK`-prefixed body as DOCX and routes it to `insertDocxFragment`, which
 * inserts the real OOXML with full fidelity.
 *
 * ## Format contract
 *
 *   read   — body starting `PK`/`UEsD` is a .docx; anything else is legacy
 *            CKEditor HTML and is converted ON OPEN.
 *   write  — ALWAYS a .docx. `getDocxBase64()` is what the slideout persists.
 *
 * Legacy HTML clauses therefore convert naturally as they are edited; ones never
 * touched keep working through the HTML branch of the insert path. No bulk
 * migration, and a converter problem surfaces on one clause rather than the
 * whole library at once.
 *
 * ## Chrome
 *
 * `toolbarChrome="formatting-basic"` — a clause is a FRAGMENT, so the document
 * menus (page breaks, TOC, tables), the track-changes mode dropdown, comment
 * threads and zoom all describe operations that do not apply; and the
 * paragraph-style picker, text colour, highlight and strikethrough describe
 * decoration that either fights the host document's theme or (strikethrough)
 * means "deleted" in a snippet that has never been part of a document. What
 * remains is what a clause author uses: undo/redo, font family/size,
 * bold/italic/underline, alignment, lists + their style pickers, indent and
 * line spacing.
 *
 * `showHeaderFooter={false}` — a clause cannot carry page furniture, so the
 * painter is told not to emit the header/footer bands at all. They are absent
 * from the DOM rather than hidden: nothing to hover, hit-test or double-click
 * into. (They previously sat ON TOP of the clause text and swallowed clicks —
 * Playwright reported `.layout-page-header intercepts pointer events` when
 * clicking into the body.)
 *
 * ## Geometry
 *
 * Everything geometric is expressed in the OOXML, never in host CSS. The layout
 * engine measures the section's page box and then paints spans at absolute
 * positions against it; resizing that box from CSS afterwards moves the box but
 * not the painted text, which then lands outside the visible area (observed:
 * `padding: 0 !important` on `.paged-editor__pages` made the clause text
 * disappear while it was still present in the DOM).
 *
 * So `clauseFragmentGeometry()` measures the container and rewrites `w:pgSz` /
 * `w:pgMar` to match it, and `pageGap={0}` butts pages flush. The result is a
 * text column exactly as wide as the slideout with no page gutters, and — with
 * zero top/bottom margins — a continuous column across page boundaries rather
 * than a visible page break.
 *
 * The stored clause therefore has a non-Letter section. That is inert:
 * `insertDocxFragment` → `docxBufferToBodyNodes` takes only the body children
 * and drops the fragment's sectPr/headers/footers, so page geometry never
 * reaches the destination document.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DocxEditor, type DocxEditorRef } from '@propdocs-editor/react';
import '@propdocs-editor/react/styles.css';
import styles from './ClauseEditor.module.scss';

export interface DocxClauseEditorRef {
  /**
   * Serialize the clause as base64 .docx — what gets persisted.
   * Returns null if the editor never mounted or serialization failed, so the
   * caller can refuse to save rather than write an empty body over real content.
   */
  getDocxBase64: () => Promise<string | null>;
  /** True once the document is loaded and editable. */
  isReady: () => boolean;
}

export interface DocxClauseEditorProps {
  /** Stored clause body: base64 .docx, or legacy base64/raw CKEditor HTML. */
  content?: string;
  /** Fires on every edit — hosts use it to enable/disable Save. */
  onContentChange?: (hasContent: boolean) => void;
  disabled?: boolean;
}

/** Decode base64 → ArrayBuffer (binary-correct). */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64.replace(/\s/g, ''));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/** Encode bytes → base64 without blowing the call stack on large buffers. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Does this body look like a .docx package rather than HTML? */
function looksLikeDocx(body: string): boolean {
  return body.startsWith('UEsD') || body.startsWith('PK');
}

/** Side page margins, in TWIPs (~0.15"). Top/bottom are zero — see below. */
const CLAUSE_SIDE_MARGIN_TWIPS = 220;
/** 1440 twips = 1 inch = 96 CSS px, so 15 twips = 1 px. */
const TWIPS_PER_PX = 15;
/**
 * Horizontal space the editor shell reserves outside the page: the outline
 * allowance it applies on BOTH sides even with the outline off
 * (`minLayoutWidth = 2 * 20 + pageWidth`). A page wider than
 * `container − 40` forces the shell into horizontal scrolling, which is the
 * "cut off on the right" the clause slideout showed.
 */
const SHELL_HORIZONTAL_RESERVE_PX = 40;
/**
 * Vertical space the shell adds around the pages: the paged viewport's top and
 * bottom padding (24 each).
 */
const SHELL_VERTICAL_RESERVE_PX = 48;
/** Toolbar height allowance — the bar is ~53px and sits above the scroll area. */
const TOOLBAR_ALLOWANCE_PX = 56;
/**
 * How many screenfuls tall the fragment page is.
 *
 * A page boundary is still a boundary even with `pageChrome="continuous"`: the
 * last line that doesn't fit moves to the next page, leaving up to one line of
 * slack at the seam. At one screenful per page that lands every ~7 lines and
 * reads as inconsistent paragraph spacing (observed in the browser). Four
 * screenfuls puts the first seam past ~28 lines, so clauses of ordinary length
 * never show one.
 *
 * The cost is scrollable white space below a short clause. That is invisible —
 * the canvas, the page and the gutter are all white and the page badge is
 * suppressed — whereas a seam is visible in the text itself.
 */
const PAGE_HEIGHT_SCREENFULS = 4;
/** Floors, so a collapsed/unmeasured container can never produce a zero page. */
const MIN_PAGE_WIDTH_PX = 240;
const MIN_PAGE_HEIGHT_PX = 640;

/**
 * Width the browser's classic scrollbars steal from a scroll container, or 0
 * where scrollbars overlay (macOS default). Measured once, lazily.
 */
let scrollbarWidthPx: number | null = null;
function measureScrollbarWidth(): number {
  if (scrollbarWidthPx !== null) return scrollbarWidthPx;
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden';
    document.body.appendChild(probe);
    scrollbarWidthPx = probe.offsetWidth - probe.clientWidth;
    probe.remove();
  } catch {
    scrollbarWidthPx = 0;
  }
  return scrollbarWidthPx;
}

interface ClauseGeometry {
  /** Page width in TWIPs. */
  width: number;
  /** Page height in TWIPs. */
  height: number;
}

/**
 * Page geometry that makes the clause fill `host` exactly.
 *
 * Width: the container minus the shell's fixed horizontal reserve and whatever
 * a vertical scrollbar would take, so the text column is as wide as the box can
 * show without ever scrolling sideways.
 *
 * Height: several screenfuls of the visible editing area, so a clause of
 * ordinary length lives on a single page and never shows a seam — see
 * PAGE_HEIGHT_SCREENFULS.
 */
function clauseFragmentGeometry(host: HTMLElement | null): ClauseGeometry {
  const hostWidth = host?.clientWidth ?? 0;
  const hostHeight = host?.clientHeight ?? 0;

  const widthPx = Math.max(MIN_PAGE_WIDTH_PX, hostWidth - SHELL_HORIZONTAL_RESERVE_PX - measureScrollbarWidth());
  const visibleHeightPx = hostHeight - TOOLBAR_ALLOWANCE_PX - SHELL_VERTICAL_RESERVE_PX;
  const heightPx = Math.max(MIN_PAGE_HEIGHT_PX, visibleHeightPx * PAGE_HEIGHT_SCREENFULS);

  return {
    width: Math.round(widthPx * TWIPS_PER_PX),
    height: Math.round(heightPx * TWIPS_PER_PX),
  };
}

/**
 * Rewrite a docx's section geometry to the fragment geometry above.
 *
 * A clause carried over from CKEditor comes out of the HTML converter with a
 * full Letter page and 1" margins, so it would open as a shrunken sheet inside
 * a slideout only a few hundred pixels wide. Normalizing on open makes
 * authoring consistent whatever the clause came from.
 *
 * Top/bottom margins go to ZERO deliberately: with `pageChrome="continuous"`
 * (which drops the page gap) that makes consecutive pages butt together into one
 * continuous column, so a long clause reads as flowing text rather than as a
 * document with page breaks in it. The visible inset above the first line comes
 * from the paged viewport's own padding.
 *
 * Section geometry never survives insertion — `docxBufferToBodyNodes` keeps only
 * the body children — so this changes presentation only, never the clause text.
 *
 * Best-effort: any failure returns the original bytes rather than risking the
 * package.
 */
async function normalizeClauseGeometry(buffer: ArrayBuffer, geometry: ClauseGeometry): Promise<ArrayBuffer> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(buffer);
    const file = zip.file('word/document.xml');
    if (!file) return buffer;

    const xml = await file.async('text');
    // Whole tags, not attribute patches: a `w:pgMar` that simply OMITS
    // `w:header` still gets Word's 0.5" default, and the layout engine then
    // grows the body margin to clear a band that does not exist — the clause
    // opened with a half-inch of dead space above the first line.
    const pgSz = `<w:pgSz w:w="${geometry.width}" w:h="${geometry.height}"/>`;
    const m = CLAUSE_SIDE_MARGIN_TWIPS;
    const pgMar =
      `<w:pgMar w:top="0" w:right="${m}" w:bottom="0" w:left="${m}" ` + `w:header="0" w:footer="0" w:gutter="0"/>`;

    // Both the self-closing and the (legal, if unusual) open/close spellings.
    let next = xml
      .replace(/<w:pgSz\b[^>]*(?:\/>|>\s*<\/w:pgSz>)/g, pgSz)
      .replace(/<w:pgMar\b[^>]*(?:\/>|>\s*<\/w:pgMar>)/g, pgMar);
    if (!/<w:sectPr\b/.test(next)) {
      // No section at all — the engine would fall back to Letter with 1"
      // margins, which is exactly what we are overriding.
      next = next.replace(/<\/w:body>/, `<w:sectPr>${pgSz}${pgMar}</w:sectPr></w:body>`);
    }
    if (next === xml) return buffer;

    zip.file('word/document.xml', next);
    return (await zip.generateAsync({ type: 'arraybuffer' })) as ArrayBuffer;
  } catch {
    return buffer;
  }
}

export const DocxClauseEditor = forwardRef<DocxClauseEditorRef, DocxClauseEditorProps>(
  ({ content = '', onContentChange, disabled = false }, ref) => {
    const editorRef = useRef<DocxEditorRef>(null);
    const hostRef = useRef<HTMLDivElement>(null);
    const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
    const [failed, setFailed] = useState(false);
    // Resolve the incoming body ONCE. After mount the editor owns the content;
    // re-resolving on a parent re-render would reload it and lose the edit.
    const resolvedRef = useRef(false);

    useEffect(() => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      let cancelled = false;

      (async () => {
        try {
          const body = (content ?? '').trim();
          // The host div renders before the editor does, so it is already laid
          // out and measurable here.
          const geometry = clauseFragmentGeometry(hostRef.current);

          // New clause, or an empty one — start from a blank document.
          if (!body) {
            const { createEmptyDocx } = await import('@propdocs-editor/core/docx');
            // Fragment geometry, not a Letter page. Set in the OOXML so the
            // stored clause agrees with what the author sees, rather than being
            // visually overridden by host CSS.
            const empty = await createEmptyDocx({
              margins: {
                top: 0,
                right: CLAUSE_SIDE_MARGIN_TWIPS,
                bottom: 0,
                left: CLAUSE_SIDE_MARGIN_TWIPS,
                // Must be 0 too — see EmptyDocxMargins.header. With Word's
                // default 720 the engine extends a 0 top margin to 48px to
                // clear a header band the clause does not have.
                header: 0,
                footer: 0,
              },
              pageSize: { width: geometry.width, height: geometry.height },
            });
            if (!cancelled) setBuffer(empty as ArrayBuffer);
            return;
          }

          if (looksLikeDocx(body)) {
            const normalized = await normalizeClauseGeometry(base64ToArrayBuffer(body), geometry);
            if (!cancelled) setBuffer(normalized);
            return;
          }

          // Legacy CKEditor clause: base64 HTML (tolerate raw HTML too).
          let html = body;
          try {
            const decoded = atob(body);
            if (/<(?:!doctype|html|body|div|p|span|table|ol|ul|h[1-6])\b/i.test(decoded)) {
              html = decoded;
            }
          } catch {
            /* not base64 — treat as raw HTML */
          }

          const { convertHtmlToDocx } = await import('../../utils/docx-migration-v2');
          const { buffer: converted } = await convertHtmlToDocx(html, window.DOMParser);
          // The converter hands back a Uint8Array view; the editor wants the
          // underlying buffer.
          const bytes = converted as unknown as Uint8Array;
          const asBuffer =
            bytes instanceof Uint8Array
              ? (bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer)
              : (converted as unknown as ArrayBuffer);
          const normalized = await normalizeClauseGeometry(asBuffer, geometry);
          if (!cancelled) setBuffer(normalized);
        } catch {
          // Never strand the author on a blank screen with no explanation —
          // and never silently open empty over content that exists.
          if (!cancelled) setFailed(true);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [content]);

    useImperativeHandle(
      ref,
      () => ({
        getDocxBase64: async () => {
          try {
            const bytes = await editorRef.current?.save?.();
            return bytes ? arrayBufferToBase64(bytes) : null;
          } catch {
            return null;
          }
        },
        isReady: () => Boolean(editorRef.current),
      }),
      []
    );

    if (failed) {
      return (
        <div className={styles['clause-editor-container']}>
          <div className={styles['clause-editor-error']} role="alert">
            This clause could not be opened for editing. Close and try again — the saved clause has not been changed.
          </div>
        </div>
      );
    }

    return (
      <div className={styles['clause-editor-container']}>
        <div className={styles['clause-editor-content']} ref={hostRef}>
          {buffer && (
            <DocxEditor
              ref={editorRef}
              documentBuffer={buffer}
              mode="editing"
              readOnly={disabled}
              // A clause fragment carries only its BODY content into the target
              // document — `insertDocxFragment` drops everything else — so a
              // comment on it has nothing durable to attach to and would be
              // silently lost. Turned off entirely rather than merely hidden.
              allowComments={false}
              toolbarChrome="formatting-basic"
              showRuler={false}
              showFileOpen={false}
              showZoomControl={false}
              // A clause has no headings to navigate — the outline rail would be
              // a permanently empty gutter stealing width from a narrow slideout.
              showOutline={false}
              showOutlineButton={false}
              // One continuous text column, not a stack of sheets: no
              // header/footer bands (a clause cannot carry page furniture, and
              // NOT painting them is what makes them un-focusable rather than
              // merely hidden), no gutter around the page, and no "page N of M"
              // badge for boundaries the author never chose.
              pageChrome="continuous"
              onChange={() => onContentChange?.(true)}
            />
          )}
        </div>
      </div>
    );
  }
);

DocxClauseEditor.displayName = 'DocxClauseEditor';

export default DocxClauseEditor;
